/* ProductionWorkflowStepper — vertical 10-step stepper for the lab SOP.
 *
 * Per the workflow spec (Layer 1):
 *   - Sequential stages with technician sign-off + timestamp
 *   - Optional stages can be skipped with a reason
 *   - QC sign-off requires a DIFFERENT technician than the fabricator
 *   - Priority (Standard / Rush / STAT) settable by admin / COE Lead
 *
 * Layer 2 (per-stage data panels) and Layer 3 (rework + on-hold) are
 * scoped for follow-up iterations. This stepper renders all 10 stages,
 * derives status from the history array, and exposes one mutating
 * action per node ("Acknowledge", "Mark complete & advance", "Rework").
 *
 * Each stage card shows:
 *   - Stage number + label
 *   - Status pill (pending / passed / failed / skipped / current)
 *   - Technician name + timestamp for completed stages
 *   - Action buttons for the current stage
 */
import React, { useEffect, useImperativeHandle, useState, forwardRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PhotoUploadField from "@/components/production/PhotoUploadField";
import {
    CheckCircle, Circle, ArrowRight, Warning, Lightning, ArrowUUpLeft,
    SkipForward, FastForward, Lock, Pause, Play, Siren, X, Clock,
    CaretDown, CaretRight,
} from "@phosphor-icons/react";
import StageDataDialog, { StageMetaDetails } from "@/components/case/StageDataDialog";


const STAGE_TONE = {
    /* iter97.19 — Lighter passed-step styling. Reduces visual weight so the
       current/upcoming stages dominate the eye. */
    passed:   { bg: "bg-emerald-50/40", border: "border-emerald-100", text: "text-emerald-700/80", chip: "bg-emerald-100/70 text-emerald-700" },
    failed:   { bg: "bg-rose-50",       border: "border-rose-200",    text: "text-rose-900",       chip: "bg-rose-100 text-rose-800" },
    skipped:  { bg: "bg-zinc-50/60",    border: "border-zinc-100",    text: "text-zinc-500",       chip: "bg-zinc-100 text-zinc-600" },
    current:  { bg: "bg-primary/5",     border: "border-primary",     text: "text-primary",        chip: "bg-primary text-white" },
    pending:  { bg: "bg-card",          border: "border-border",      text: "text-muted-foreground", chip: "bg-zinc-100 text-zinc-500" },
};


function deriveStageStatus(stageSlug, currentStage, history, allStages) {
    /* Pure function: given a stage slug, the current stage, and the full
       history array, decide what status to render. The history array is
       append-only — we walk it backwards looking for the most recent
       entry where this stage was the `from_stage` (i.e., the tech
       finished it).

       Position fallback: if no history matches this stage but the stage
       is strictly BEFORE the current stage in the canonical order, mark
       it 'passed'. This keeps backfilled cases (no history rows) from
       looking like the tech skipped the early steps. */
    if (stageSlug === currentStage) return "current";
    // Find most recent transition where this was from_stage (= passed/skipped/failed)
    const rev = [...history].reverse();
    const lastTransition = rev.find(
        (h) => h.from_stage === stageSlug && h.to_stage !== stageSlug,
    );
    if (lastTransition) {
        return lastTransition.status === "skipped" ? "skipped" : "passed";
    }
    // Any failed mark on this exact stage?
    const lastFail = rev.find(
        (h) => h.from_stage === stageSlug && h.status === "failed",
    );
    if (lastFail) return "failed";
    // Position fallback: stage strictly before current → passed
    if (Array.isArray(allStages)) {
        const slugs = allStages.map((s) => s.slug);
        const idxThis = slugs.indexOf(stageSlug);
        const idxCurrent = slugs.indexOf(currentStage);
        if (idxThis >= 0 && idxCurrent >= 0 && idxThis < idxCurrent) {
            return "passed";
        }
    }
    return "pending";
}


/* iter97.28 — Step 1 info pane. Renders ONLY when Step 1
   ("Case Assignment & File Reception") is the current stage and the tech
   has not yet acknowledged the case. Purely read-only — the technician
   uses the top-level "Acknowledge & begin" button on
   ProductionCaseDetail to advance. */
function Step1AssignmentInfo({ caseObj }) {
    const c = caseObj || {};
    const spec = c.manufacturing_spec || {};
    const plan = c.plan || {};
    const stlFiles = (c.files || []).filter((f) =>
        (f.filename || f.name || "").toLowerCase().match(/\.(stl|obj|ply)$/),
    );

    const archesLabel = (() => {
        const a = (spec.arches || c.arches || "").toLowerCase();
        if (a === "upper") return "Upper only";
        if (a === "lower") return "Lower only";
        if (a === "both") return "Upper + Lower";
        const u = Number(plan.aligner_count_upper || 0);
        const l = Number(plan.aligner_count_lower || 0);
        if (u > 0 && l > 0) return "Upper + Lower";
        if (u > 0) return "Upper only";
        if (l > 0) return "Lower only";
        return "—";
    })();
    const stages = Number(spec.aligner_stages_count || 0);
    const sps = Number(spec.sheets_per_stage || 1);
    const totalSheets = stages * sps;
    const phases = Array.isArray(spec.phases) ? spec.phases : [];

    /* Helper for a single key/value row */
    const Row = ({ k, v }) => (
        <div className="flex items-start justify-between gap-3 text-xs border-b border-border/40 pb-1 last:border-b-0">
            <span className="text-muted-foreground shrink-0">{k}</span>
            <span className="font-semibold text-foreground text-right break-all">{v}</span>
        </div>
    );

    return (
        <div className="mt-3 space-y-3" data-testid="workflow-step1-info">
            {/* Case details */}
            <div className="rounded-lg border border-border bg-card/60 overflow-hidden" data-testid="workflow-step1-case-details">
                <div className="px-3 py-1.5 bg-secondary/40 border-b border-border text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                    Case details
                </div>
                <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                    <Row k="Case ID" v={<span className="font-mono">{c.code || c.id?.slice(0, 8) || "—"}</span>} />
                    <Row k="Patient" v={c.patient_name || "—"} />
                    <Row k="Doctor" v={c.doctor_name ? `Dr. ${c.doctor_name.replace(/^Dr\.\s*/i, "")}` : "—"} />
                    <Row k="Clinic" v={c.practice_location_name || c.clinic_name || c.doctor_clinic || "—"} />
                    {c.diagnosis && !/^TEST[_\s]/i.test(c.diagnosis.trim()) && (
                        <Row k="Diagnosis" v={c.diagnosis} />
                    )}
                    {(c.chief_complaint || c.clinical?.chief_complaint) && (
                        <Row k="Chief complaint" v={c.chief_complaint || c.clinical?.chief_complaint} />
                    )}
                    <Row k="Arches" v={archesLabel} />
                    {(plan.aligner_count_upper != null || plan.aligner_count_lower != null) && (
                        <Row k="Aligners on plan" v={`U${plan.aligner_count_upper || 0} · L${plan.aligner_count_lower || 0}`} />
                    )}
                </div>
            </div>

            {/* STL files */}
            <div className="rounded-lg border border-border bg-card/60 overflow-hidden" data-testid="workflow-step1-stl-files">
                <div className="px-3 py-1.5 bg-secondary/40 border-b border-border text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground flex items-center justify-between">
                    <span>STL files</span>
                    <span className="font-mono tabular-nums">{stlFiles.length}</span>
                </div>
                {stlFiles.length === 0 ? (
                    <div className="p-3 text-[11px] text-muted-foreground italic">No STL / OBJ / PLY files attached yet.</div>
                ) : (
                    <ul className="divide-y divide-border/60">
                        {stlFiles.map((f, i) => (
                            <li
                                key={f.id || f.url || i}
                                className="px-3 py-1.5 text-xs flex items-center justify-between gap-2"
                                data-testid={`workflow-step1-stl-file-${i}`}
                            >
                                <span className="font-mono truncate" title={f.filename || f.name}>
                                    {f.filename || f.name || `File ${i + 1}`}
                                </span>
                                {f.size_kb && (
                                    <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
                                        {Number(f.size_kb).toLocaleString("en-IN")} KB
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Manufacturing spec summary */}
            <div className="rounded-lg border border-border bg-card/60 overflow-hidden" data-testid="workflow-step1-spec-summary">
                <div className="px-3 py-1.5 bg-secondary/40 border-b border-border text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                    Manufacturing spec summary
                </div>
                <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                    {spec.sheet_name && <Row k="Aligner sheet" v={spec.sheet_name} />}
                    {spec.retainer_sku && <Row k="Retainer sheet" v={<span className="font-mono">{spec.retainer_sku}</span>} />}
                    {stages > 0 && <Row k="Aligner stages" v={`${stages} stage${stages === 1 ? "" : "s"}`} />}
                    {sps > 0 && <Row k="Sheets per stage" v={sps} />}
                    {totalSheets > 0 && <Row k="Total sheets" v={`${totalSheets} (${stages}×${sps})`} />}
                    {Number(spec.retainer_count || 0) > 0 && <Row k="Retainers" v={`${spec.retainer_count} sheet${spec.retainer_count === 1 ? "" : "s"}`} />}
                    {phases.length > 0 && (
                        <Row
                            k="Dispatch phases"
                            v={
                                <span className="text-right">
                                    {phases.map((p, i) => (
                                        <span key={i} className="block">
                                            Phase {i + 1}: stages {p.stage_from}–{p.stage_to}
                                        </span>
                                    ))}
                                </span>
                            }
                        />
                    )}
                    {spec.notes && spec.notes.trim() && (
                        <div className="sm:col-span-2 text-xs">
                            <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-muted-foreground mt-1 mb-0.5">Planner spec notes</div>
                            <div className="text-foreground whitespace-pre-wrap leading-snug bg-secondary/30 rounded px-2 py-1.5">
                                {spec.notes}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="text-[11px] text-muted-foreground italic px-1" data-testid="workflow-step1-cta-hint">
                When you&apos;re ready, click <span className="font-semibold text-foreground">Acknowledge &amp; begin</span> above to start the pre-production gate.
            </div>
        </div>
    );
}


/* iter97.19 — Render one stage card. Extracted from the JSX inline so the
   collapsible-passed-steps wrapper can call it per-row without copy-paste.
   All event handlers are passed in so this stays a pure renderer. */
function renderStage({ s, idx, status, current, history, wf, busy, isAck, caseObj, onAdvanceClick, setSkipsOpen, setReworkOpen, setHoldOpen, setEscalateOpen, setAckOpen }) {
    const tone = STAGE_TONE[status];
    const isCurrent = status === "current";
    const entries = (history || []).filter((h) => h.from_stage === s.slug || h.to_stage === s.slug);
    const lastEntry = entries.length ? entries[entries.length - 1] : null;
    const lastPassEntry = [...entries].reverse().find((h) =>
        h.from_stage === s.slug && h.to_stage !== s.slug,
    );
    /* iter97.19 — Lighter visual weight on passed/skipped stages: thinner
       border, dimmer text, smaller padding. Current/pending stages keep
       the original prominence. */
    const isHistorical = status === "passed" || status === "skipped";
    const borderClass = isHistorical ? "border" : "border-2";
    const padClass = isHistorical ? "p-3" : "p-4";
    return (
        <li
            key={s.slug}
            className={`rounded-xl ${borderClass} ${tone.border} ${tone.bg} ${padClass} flex items-start gap-3 ${isHistorical ? "opacity-80" : ""}`}
            data-testid={`workflow-stage-${s.slug}`}
            data-stage-status={status}
        >
            {/* Stage number bubble */}
            <div className={`${isHistorical ? "w-6 h-6" : "w-8 h-8"} rounded-full grid place-items-center shrink-0 ${
                status === "passed" ? "bg-emerald-500 text-white" :
                status === "current" ? "bg-primary text-primary-foreground ring-4 ring-primary/20" :
                status === "failed" ? "bg-rose-500 text-white" :
                status === "skipped" ? "bg-zinc-400 text-white" :
                "bg-zinc-200 text-zinc-500"
            } font-bold text-xs`}>
                {status === "passed" ? <CheckCircle size={isHistorical ? 12 : 14} weight="fill" /> :
                 status === "skipped" ? <FastForward size={isHistorical ? 10 : 12} weight="fill" /> :
                 status === "failed" ? "!" :
                 idx + 1}
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className={`font-display font-semibold ${isHistorical ? "text-xs" : "text-sm"} ${tone.text}`}>
                        {s.label}
                    </span>
                    {s.optional && (
                        <span className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground px-1.5 py-0.5 rounded bg-zinc-100">
                            Optional
                        </span>
                    )}
                    <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${tone.chip}`}>
                        {status === "current" ? "In progress" : status}
                    </span>
                </div>
                {lastPassEntry && (
                    <div className="text-[11px] text-muted-foreground">
                        {lastPassEntry.status === "skipped"
                            ? <>Skipped {lastPassEntry.notes && <span className="italic">— "{lastPassEntry.notes}"</span>}</>
                            : <>by <span className="font-semibold text-foreground">{lastPassEntry.technician_name}</span> · {new Date(lastPassEntry.timestamp).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</>}
                    </div>
                )}
                {lastEntry?.is_rework && lastEntry.to_stage === s.slug && (
                    <div className="text-[10px] mt-1 inline-flex items-center gap-1 text-amber-700 font-semibold bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                        <ArrowUUpLeft size={9} weight="bold" /> Reworked from {lastEntry.from_stage_label}
                    </div>
                )}
                {/* Layer-2: show captured meta (printer ID, resin batch, QC
                    checklist, etc.) under completed stages so the audit
                    trail is readable at a glance. */}
                {!isHistorical && lastPassEntry?.meta && Object.keys(lastPassEntry.meta).length > 0 && (
                    <StageMetaDetails
                        meta={lastPassEntry.meta}
                        fields={wf?.stage_meta_schemas?.[s.slug]?.fields}
                    />
                )}
                {isCurrent && wf.acknowledged_at && s.slug === "assigned" && (
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                        Acknowledged by <span className="font-semibold text-foreground">{wf.acknowledged_by_name}</span> · {new Date(wf.acknowledged_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </div>
                )}
                {/* Layer-4 SLA chip — visible only on the CURRENT
                    stage. Surfaces hours-in-stage + the SLA
                    target so the tech knows when they're
                    running late before the dashboard flags it. */}
                {isCurrent && wf.time_in_stage && wf.time_in_stage.target_hours != null && (
                    <div
                        className={`inline-flex items-center gap-1.5 mt-2 px-2 py-0.5 rounded text-[10px] font-semibold border ${
                            wf.time_in_stage.sla_status === "breached"
                                ? "bg-rose-50 text-rose-800 border-rose-200"
                                : wf.time_in_stage.sla_status === "warning"
                                ? "bg-amber-50 text-amber-800 border-amber-200"
                                : "bg-emerald-50 text-emerald-800 border-emerald-200"
                        }`}
                        data-testid={`workflow-sla-${s.slug}`}
                    >
                        <Clock size={10} weight="bold" />
                        {wf.time_in_stage.hours_in_stage}h / {wf.time_in_stage.target_hours}h target
                        {wf.time_in_stage.sla_status === "breached" && <span className="uppercase font-bold ml-1">SLA breached</span>}
                    </div>
                )}

                {/* iter97.28 — Step 1 is purely informational. The technician
                    reads the case details, STL files list, and manufacturing
                    spec summary below, then uses the top-level
                    "Acknowledge & begin" button (header on
                    ProductionCaseDetail) to start the pre-production gate.
                    No action button lives inside this card. */}
                {isCurrent && !isAck && s.slug === "assigned" && caseObj && (
                    <Step1AssignmentInfo caseObj={caseObj} />
                )}

                {/* Active stage actions */}
                {isCurrent && isAck && s.slug !== "closed" && (
                    <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                            size="sm"
                            onClick={() => onAdvanceClick(s.slug)}
                            disabled={busy || wf.on_hold}
                            title={wf.on_hold ? "Resume from hold before advancing" : ""}
                            className="rounded-full"
                            data-testid={`workflow-advance-${s.slug}`}
                        >
                            <CheckCircle size={12} className="mr-1.5" weight="fill" />
                            Mark complete & advance
                            <ArrowRight size={12} className="ml-1.5" />
                        </Button>
                        {(s.slug === "thermoforming" || s.slug === "model_production") && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setSkipsOpen(true)}
                                disabled={busy || wf.on_hold}
                                className="rounded-full"
                                data-testid={`workflow-skip-${s.slug}`}
                            >
                                <SkipForward size={12} className="mr-1.5" />
                                Skip optional stages
                            </Button>
                        )}
                        {idx > 0 && (
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setReworkOpen(true)}
                                disabled={busy || wf.on_hold}
                                className="rounded-full text-rose-700 hover:bg-rose-50"
                                data-testid={`workflow-rework-${s.slug}`}
                            >
                                <ArrowUUpLeft size={12} className="mr-1.5" />
                                Rework — rewind to earlier stage
                            </Button>
                        )}
                        {/* Layer-3 actions — Hold + Escalate. Hidden once
                            the case is already on hold (the banner above
                            carries the Resume button instead). */}
                        {!wf.on_hold && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setHoldOpen(true)}
                                disabled={busy}
                                className="rounded-full text-amber-700 hover:bg-amber-50 border-amber-200"
                                data-testid={`workflow-hold-${s.slug}`}
                            >
                                <Pause size={12} className="mr-1.5" />
                                Mark on hold
                            </Button>
                        )}
                        {!wf.escalation_active && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEscalateOpen(true)}
                                disabled={busy || wf.on_hold}
                                title={wf.on_hold ? "Resume from hold before raising an escalation" : ""}
                                className="rounded-full text-rose-700 hover:bg-rose-50 border-rose-200"
                                data-testid={`workflow-escalate-${s.slug}`}
                            >
                                <Siren size={12} className="mr-1.5" />
                                Escalate to admin
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </li>
    );
}


function ProductionWorkflowStepper({ caseId, caseObj, onChange }, ref) {
    const [wf, setWf] = useState(null);
    const [busy, setBusy] = useState(false);
    const [ackOpen, setAckOpen] = useState(false);
    const [reworkOpen, setReworkOpen] = useState(false);
    const [skipsOpen, setSkipsOpen] = useState(false);
    // Layer-2 — data dialog opens when the active stage has a schema.
    const [dataDialogStage, setDataDialogStage] = useState(null);
    // Layer-3 — hold / escalation dialogs
    const [holdOpen, setHoldOpen] = useState(false);
    const [escalateOpen, setEscalateOpen] = useState(false);
    // iter97.19 — collapse passed/skipped steps so current+upcoming dominate.
    const [passedCollapsed, setPassedCollapsed] = useState(true);

    /* iter97.23 — Expose openAckDialog() so the parent header button
       ("Acknowledge & begin" on ProductionCaseDetail) can trigger the
       same ack dialog without us duplicating the flow. */
    useImperativeHandle(ref, () => ({
        openAckDialog: () => setAckOpen(true),
    }));

    const load = () => {
        if (!caseId) return;
        api.get(`/production/cases/${caseId}/workflow`)
            .then((r) => setWf(r.data))
            .catch(() => setWf(null));
    };
    useEffect(() => { load(); }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!wf) {
        return <div className="text-muted-foreground text-sm py-6">Loading workflow…</div>;
    }

    const stages = wf.stages || [];
    const current = wf.current_stage;
    const history = wf.history || [];
    const isAck = !!wf.acknowledged_at;

    const advance = async ({ to_stage, skipped_stages = [], skip_reason = "", notes = "", status = "passed", meta = {} } = {}) => {
        if (busy) return;
        setBusy(true);
        try {
            const r = await api.post(`/production/cases/${caseId}/advance`, {
                to_stage, skipped_stages, skip_reason, notes, status, meta,
            });
            setWf(r.data);
            onChange?.();
            const newStage = r.data?.current_stage;
            const label = (r.data?.stages || []).find((s) => s.slug === newStage)?.label || newStage;
            toast.success(`Advanced to ${label}`);
        } catch (e) {
            // Surface validation detail when backend rejects missing fields
            const det = e.response?.data?.detail;
            const msg = typeof det === "string" ? det
                : det?.message ? det.message
                : "Could not advance";
            toast.error(msg);
        } finally { setBusy(false); }
    };

    /* Click on "Mark complete & advance" — if the current stage has a
       Layer-2 schema we open the data dialog first; otherwise we
       advance directly. The dialog itself triggers `advance` once the
       tech fills the required fields. */
    const onAdvanceClick = (stageSlug) => {
        const hasSchema = !!wf?.stage_meta_schemas?.[stageSlug]?.fields;
        if (hasSchema) {
            setDataDialogStage(stageSlug);
        } else {
            advance({});
        }
    };

    const rework = async ({ to_stage, reason, failed_qc_items = [] }) => {
        if (busy) return;
        setBusy(true);
        try {
            const r = await api.post(`/production/cases/${caseId}/rework`, { to_stage, reason, failed_qc_items });
            setWf(r.data);
            onChange?.();
            toast.success("Stage rewound for rework");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not rework");
        } finally { setBusy(false); }
    };

    /* Layer-3 — hold / resume / escalate helpers. Each updates the
       workflow state in-place so the banner updates without a reload. */
    const hold = async ({ kind, reason }) => {
        if (busy) return; setBusy(true);
        try {
            const r = await api.post(`/production/cases/${caseId}/hold`, { kind, reason });
            setWf(r.data); onChange?.();
            toast.success(`Case marked on hold (${kind})`);
            setHoldOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not mark on hold");
        } finally { setBusy(false); }
    };
    const resume = async () => {
        if (busy) return; setBusy(true);
        try {
            const r = await api.post(`/production/cases/${caseId}/resume`, {});
            setWf(r.data); onChange?.();
            toast.success("Resumed from hold");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not resume");
        } finally { setBusy(false); }
    };
    const escalate = async ({ reason, severity, issue_type, attachment_file_ids }) => {
        if (busy) return; setBusy(true);
        try {
            const r = await api.post(`/production/cases/${caseId}/escalate`, {
                reason, severity, issue_type, attachment_file_ids,
            });
            setWf(r.data); onChange?.();
            toast.success("Admin notified");
            setEscalateOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not escalate");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="production-workflow-stepper">
            {/* On-hold banner — Layer 3. Sticky red strip while case is paused. */}
            {wf.on_hold && (
                <div className="rounded-2xl border-2 border-rose-300 bg-rose-50 p-4 mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid="workflow-hold-banner">
                    <div className="flex items-start gap-3">
                        <Pause size={20} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />
                        <div>
                            <div className="font-semibold text-sm text-rose-900">
                                On hold — <span className="uppercase tracking-wider text-[10px] bg-rose-200 px-1.5 py-0.5 rounded">{wf.on_hold_kind || "other"}</span>
                            </div>
                            <div className="text-xs text-rose-800/90 mt-1">{wf.on_hold_reason}</div>
                            <div className="text-[10px] text-rose-700/70 mt-0.5">
                                Held by {wf.on_hold_by_name || "—"} · {wf.on_hold_at ? new Date(wf.on_hold_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                            </div>
                        </div>
                    </div>
                    <Button onClick={resume} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="workflow-resume-button">
                        <Play size={12} className="mr-1.5" weight="fill" /> Resume
                    </Button>
                </div>
            )}

            {/* Escalation banner — Layer 3. Amber strip until admin clears it. */}
            {wf.escalation_active && (
                <div className={`rounded-2xl border-2 ${wf.escalation_severity === "critical" ? "border-rose-400 bg-rose-50" : "border-amber-300 bg-amber-50"} p-4 mb-4 flex items-start gap-3`} data-testid="workflow-escalation-banner">
                    <Siren size={20} weight="fill" className={wf.escalation_severity === "critical" ? "text-rose-600 shrink-0 mt-0.5" : "text-amber-600 shrink-0 mt-0.5"} />
                    <div className="flex-1 min-w-0">
                        <div className={`font-semibold text-sm ${wf.escalation_severity === "critical" ? "text-rose-900" : "text-amber-900"}`}>
                            Escalated to admin {wf.escalation_severity === "critical" && <span className="ml-1 uppercase tracking-wider text-[10px] bg-rose-200 text-rose-900 px-1.5 py-0.5 rounded">Critical</span>}
                        </div>
                        <div className={`text-xs mt-1 ${wf.escalation_severity === "critical" ? "text-rose-800/90" : "text-amber-800/90"}`}>{wf.escalation_reason}</div>
                        <div className={`text-[10px] mt-0.5 ${wf.escalation_severity === "critical" ? "text-rose-700/70" : "text-amber-700/70"}`}>
                            Raised by {wf.escalation_by_name || "—"} · {wf.escalation_at ? new Date(wf.escalation_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""} · Awaiting admin review
                        </div>
                    </div>
                </div>
            )}

            {/* Pending QC failures from a prior rework — Layer 3.
                When a case returns to qc_review after a rework that flagged
                specific checklist items as failed, surface those items here
                so the next inspector knows exactly what to re-verify. */}
            {current === "qc_review" && wf.pending_qc_failures && wf.pending_qc_failures.length > 0 && (
                <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 mb-4 flex items-start gap-3" data-testid="workflow-pending-qc-banner">
                    <Warning size={20} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-amber-900">
                            {wf.pending_qc_failures.length} item{wf.pending_qc_failures.length === 1 ? "" : "s"} from prior QC need re-verification
                        </div>
                        <ul className="text-xs text-amber-800/90 mt-1.5 space-y-0.5 list-disc list-inside">
                            {wf.pending_qc_failures.map((slug) => {
                                const fld = wf.stage_meta_schemas?.qc_review?.fields?.[slug];
                                return <li key={slug} data-testid={`workflow-pending-qc-${slug}`}>{fld?.label || slug}</li>;
                            })}
                        </ul>
                    </div>
                </div>
            )}

            {/* iter97.23 — Removed the floating "Acknowledge case receipt" banner.
                Step 1 ("Case Assignment & File Reception") in the stage list
                below now serves as the acknowledgement step (it shows
                IN PROGRESS + timer naturally). The header "Acknowledge & begin"
                button on ProductionCaseDetail drives the ack action. */}

            {/* Stage cards */}
            <ol className="space-y-2" data-testid="workflow-stage-list">
                {(() => {
                    /* iter97.19 — Compute statuses up-front so we can wrap
                       the passed run into a single collapsible chip when
                       there's more than 1. Keeps the eye on current +
                       upcoming work without losing the audit history. */
                    const stageStatuses = stages.map((s) => deriveStageStatus(s.slug, current, history, stages));
                    const currentIdx = stageStatuses.findIndex((st) => st === "current");
                    // Run of passed/skipped stages BEFORE the current one.
                    let collapseEnd = -1;
                    if (currentIdx > 0) {
                        let i = 0;
                        while (i < currentIdx && (stageStatuses[i] === "passed" || stageStatuses[i] === "skipped")) {
                            collapseEnd = i;
                            i += 1;
                        }
                    }
                    const collapseCount = collapseEnd >= 0 ? collapseEnd + 1 : 0;
                    const shouldCollapse = passedCollapsed && collapseCount >= 2;
                    const rendered = [];
                    if (shouldCollapse) {
                        rendered.push(
                            <li
                                key="__collapsed_passed__"
                                className="rounded-xl border border-dashed border-emerald-200/80 bg-emerald-50/40 px-4 py-2.5 flex items-center justify-between gap-3 cursor-pointer hover:bg-emerald-50/70 transition-colors"
                                onClick={() => setPassedCollapsed(false)}
                                data-testid="workflow-passed-collapsed"
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                                        <CheckCircle size={14} weight="fill" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-xs font-semibold text-emerald-900">
                                            {collapseCount} step{collapseCount === 1 ? "" : "s"} completed
                                        </div>
                                        <div className="text-[10px] text-emerald-700/80 truncate">
                                            {stages.slice(0, collapseEnd + 1).map((s) => s.label).join(" · ")}
                                        </div>
                                    </div>
                                </div>
                                <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-700 font-semibold whitespace-nowrap">
                                    Show <CaretDown size={10} weight="bold" />
                                </span>
                            </li>
                        );
                    } else if (collapseCount >= 2) {
                        rendered.push(
                            <li
                                key="__hide_passed__"
                                className="text-[10px] text-muted-foreground hover:text-foreground cursor-pointer pl-2"
                                onClick={() => setPassedCollapsed(true)}
                                data-testid="workflow-passed-hide"
                            >
                                <span className="inline-flex items-center gap-0.5">
                                    Hide {collapseCount} completed step{collapseCount === 1 ? "" : "s"} <CaretRight size={10} weight="bold" />
                                </span>
                            </li>
                        );
                    }
                    stages.forEach((s, idx) => {
                        if (shouldCollapse && idx <= collapseEnd) return;
                        const status = stageStatuses[idx];
                        rendered.push(renderStage({
                            s, idx, status, current, history, wf,
                            busy, isAck, caseObj,
                            onAdvanceClick, setSkipsOpen, setReworkOpen,
                            setHoldOpen, setEscalateOpen, setAckOpen,
                        }));
                    });
                    return rendered;
                })()}
            </ol>

            {/* Dialogs */}
            <AcknowledgeDialog
                open={ackOpen}
                onClose={() => setAckOpen(false)}
                onAck={async ({ file_integrity_verified, production_batch_number, notes }) => {
                    setBusy(true);
                    try {
                        const r = await api.post(`/production/cases/${caseId}/acknowledge`, {
                            file_integrity_verified, production_batch_number, notes,
                        });
                        setWf(r.data); onChange?.();
                        toast.success("Case acknowledged — production clock started");
                        setAckOpen(false);
                    } catch (e) {
                        toast.error(e.response?.data?.detail || "Failed");
                    } finally { setBusy(false); }
                }}
            />

            <SkipOptionalsDialog
                open={skipsOpen}
                onClose={() => setSkipsOpen(false)}
                stages={stages}
                onAdvance={async ({ to_stage, skipped_stages, skip_reason }) => {
                    await advance({ to_stage, skipped_stages, skip_reason });
                    setSkipsOpen(false);
                }}
            />

            <ReworkDialog
                open={reworkOpen}
                onClose={() => setReworkOpen(false)}
                stages={stages}
                currentStage={current}
                qcSchema={wf?.stage_meta_schemas?.qc_review}
                onRework={async (payload) => {
                    await rework(payload);
                    setReworkOpen(false);
                }}
            />

            <StageDataDialog
                open={!!dataDialogStage}
                stageSlug={dataDialogStage}
                schemas={wf?.stage_meta_schemas}
                busy={busy}
                caseId={caseId}
                onClose={() => setDataDialogStage(null)}
                onSubmit={async ({ meta }) => {
                    await advance({ meta });
                    // Close only if advance succeeded — `busy` toggles back to false
                    // after the request settles, regardless of outcome. We rely on
                    // the toast-error path to keep the dialog open when validation
                    // fails so the tech can fix and re-submit.
                    setDataDialogStage(null);
                }}
            />

            <HoldDialog
                open={holdOpen}
                onClose={() => setHoldOpen(false)}
                busy={busy}
                onHold={hold}
            />

            <EscalateDialog
                open={escalateOpen}
                onClose={() => setEscalateOpen(false)}
                busy={busy}
                onEscalate={escalate}
                caseId={caseId}
            />
        </div>
    );
}

/* iter97.23 — Wrap in forwardRef so the parent can call openAckDialog()
   on this component's ref (header "Acknowledge & begin" button). */
export default forwardRef(ProductionWorkflowStepper);


/* ─── Acknowledge dialog — Stage 1 receipt with file-integrity check */
function AcknowledgeDialog({ open, onClose, onAck }) {
    const [verified, setVerified] = useState(false);
    const [batch, setBatch] = useState("");
    const [notes, setNotes] = useState("");
    const submit = () => {
        if (!verified) {
            toast.error("Please confirm file integrity first");
            return;
        }
        onAck({ file_integrity_verified: verified, production_batch_number: batch, notes });
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="workflow-ack-dialog">
                <DialogHeader>
                    <DialogTitle>Acknowledge case</DialogTitle>
                    <DialogDescription>
                        Confirm you've reviewed the case file and the STL scans are present and uncorrupted.
                    </DialogDescription>
                </DialogHeader>
                <label className="flex items-start gap-2 p-3 rounded-xl border border-border bg-card cursor-pointer hover:bg-secondary/30">
                    <Checkbox checked={verified} onCheckedChange={setVerified} className="mt-0.5" data-testid="workflow-ack-verify" />
                    <span className="text-sm">
                        <span className="font-semibold">Files verified</span> — STL scans are present, the treatment plan matches the prescription, and all stages are accounted for.
                    </span>
                </label>
                <div>
                    <Label className="text-xs">Production batch number (optional)</Label>
                    <Input value={batch} onChange={(e) => setBatch(e.target.value)} placeholder="BATCH-2026-0142" data-testid="workflow-ack-batch" />
                </div>
                <div>
                    <Label className="text-xs">Notes (optional)</Label>
                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Any flag worth recording for the audit log…" data-testid="workflow-ack-notes" />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={!verified} data-testid="workflow-ack-submit">
                        Acknowledge receipt
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* ─── Skip-optionals dialog — let tech advance through optional stages */
function SkipOptionalsDialog({ open, onClose, stages, onAdvance }) {
    const optionals = (stages || []).filter((s) => s.optional);
    const [picked, setPicked] = useState({});
    const [reason, setReason] = useState("");
    const targets = (stages || []).filter((s) => !s.optional);
    const [target, setTarget] = useState("");

    useEffect(() => {
        if (open) {
            setPicked({});
            setReason("");
            setTarget("");
        }
    }, [open]);

    const toggle = (slug) => setPicked((p) => ({ ...p, [slug]: !p[slug] }));
    const skipped_stages = Object.keys(picked).filter((k) => picked[k]);

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="workflow-skip-dialog">
                <DialogHeader>
                    <DialogTitle>Skip optional stages</DialogTitle>
                    <DialogDescription>
                        Mark trimming or cleaning as skipped (e.g. pre-cleaned material). They'll appear in the audit log as "skipped" with your reason.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                    {optionals.map((s) => (
                        <label key={s.slug} className="flex items-center gap-2 p-2.5 rounded-lg border border-border hover:bg-secondary/30 cursor-pointer">
                            <Checkbox checked={!!picked[s.slug]} onCheckedChange={() => toggle(s.slug)} data-testid={`workflow-skip-opt-${s.slug}`} />
                            <span className="text-sm font-semibold">{s.label}</span>
                        </label>
                    ))}
                </div>
                <div>
                    <Label className="text-xs">Advance to</Label>
                    <select
                        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                        value={target}
                        onChange={(e) => setTarget(e.target.value)}
                        data-testid="workflow-skip-target"
                    >
                        <option value="">Select next stage…</option>
                        {targets.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
                    </select>
                </div>
                <div>
                    <Label className="text-xs">Reason for skipping</Label>
                    <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="e.g. Pre-cleaned material, no trimming needed" data-testid="workflow-skip-reason" />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button
                        onClick={() => onAdvance({ to_stage: target, skipped_stages, skip_reason: reason })}
                        disabled={!target || skipped_stages.length === 0 || !reason.trim()}
                        data-testid="workflow-skip-submit"
                    >
                        Skip & advance
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* ─── Rework dialog — rewind to an earlier stage with reason.
   At qc_review, also surfaces the 9 QC checklist items so the inspector
   can tick which ones failed; the slugs travel to the next QC attempt
   as `pending_qc_failures` (Layer 3). */
function ReworkDialog({ open, onClose, stages, currentStage, qcSchema, onRework }) {
    const [to, setTo] = useState("");
    const [reason, setReason] = useState("");
    const [failedItems, setFailedItems] = useState({});
    const stageIdx = (slug) => (stages || []).findIndex((s) => s.slug === slug);
    const earlier = (stages || []).filter((s, i) => i < stageIdx(currentStage));
    const isQcRework = currentStage === "qc_review";
    const qcFields = isQcRework ? (qcSchema?.fields || {}) : {};
    const checklistItems = Object.entries(qcFields).filter(([k]) => k.startsWith("qc_") && k !== "qc_inspector_notes");

    useEffect(() => {
        if (open) { setTo(""); setReason(""); setFailedItems({}); }
    }, [open]);

    const toggleItem = (slug) => setFailedItems((p) => ({ ...p, [slug]: !p[slug] }));
    const failed_qc_items = Object.keys(failedItems).filter((k) => failedItems[k]);

    const canSubmit = !!to && reason.trim().length >= 5
        && (!isQcRework || failed_qc_items.length > 0);

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="workflow-rework-dialog">
                <DialogHeader>
                    <DialogTitle>Rework — rewind to earlier stage</DialogTitle>
                    <DialogDescription>
                        {isQcRework
                            ? "Tick the QC items that failed inspection. They'll surface at the top of the QC stage when the case returns."
                            : "Use this when a defect needs a re-print or re-form. The rewind is logged with your reason and remains in the audit trail."}
                    </DialogDescription>
                </DialogHeader>
                {isQcRework && (
                    <div className="border border-rose-200 rounded-lg bg-rose-50/50 p-3 space-y-1.5" data-testid="rework-qc-checklist">
                        <div className="text-xs font-bold uppercase tracking-wider text-rose-900 mb-1">Which inspections failed?</div>
                        {checklistItems.map(([slug, spec]) => (
                            <label key={slug} className="flex items-start gap-2 cursor-pointer text-xs">
                                <Checkbox
                                    checked={!!failedItems[slug]}
                                    onCheckedChange={() => toggleItem(slug)}
                                    className="mt-0.5"
                                    data-testid={`rework-qc-item-${slug}`}
                                />
                                <span className="text-rose-900/90 leading-tight">{spec.label}</span>
                            </label>
                        ))}
                    </div>
                )}
                <div>
                    <Label className="text-xs">Rewind to</Label>
                    <select
                        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        data-testid="workflow-rework-target"
                    >
                        <option value="">Select earlier stage…</option>
                        {earlier.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
                    </select>
                </div>
                <div>
                    <Label className="text-xs">Reason (required)</Label>
                    <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="e.g. UR3 model delaminated during cure, need to reprint stage 4" data-testid="workflow-rework-reason" />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button
                        onClick={() => onRework({ to_stage: to, reason, failed_qc_items })}
                        disabled={!canSubmit}
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                        data-testid="workflow-rework-submit"
                    >
                        <ArrowUUpLeft size={12} className="mr-1.5" /> Rewind & log rework
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* ─── Hold dialog — Layer 3. Categorise the hold for trend analysis. */
function HoldDialog({ open, onClose, busy, onHold }) {
    const [kind, setKind] = useState("");
    const [reason, setReason] = useState("");
    useEffect(() => { if (open) { setKind(""); setReason(""); } }, [open]);
    const KINDS = [
        { slug: "equipment", label: "Equipment failure",       sub: "Printer / thermoformer / cleaner is down" },
        { slug: "material",  label: "Material issue",          sub: "Resin / sheet defect or supply gap" },
        { slug: "file",      label: "File / data issue",       sub: "Corrupt STL / missing stage / prescription conflict" },
        { slug: "other",     label: "Other",                    sub: "Specify in the reason below" },
    ];
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="workflow-hold-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Pause size={16} weight="fill" className="text-rose-600" />
                        Mark case on hold
                    </DialogTitle>
                    <DialogDescription>
                        Work pauses but the case stays in the queue with a red badge so it isn't forgotten.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-1.5">
                    {KINDS.map((k) => (
                        <label key={k.slug} className={`flex items-start gap-2 p-2.5 rounded-lg border ${kind === k.slug ? "border-rose-400 bg-rose-50/50" : "border-border"} cursor-pointer hover:bg-secondary/30`}>
                            <input
                                type="radio"
                                name="hold-kind"
                                value={k.slug}
                                checked={kind === k.slug}
                                onChange={(e) => setKind(e.target.value)}
                                className="mt-0.5"
                                data-testid={`hold-kind-${k.slug}`}
                            />
                            <div>
                                <div className="text-sm font-semibold">{k.label}</div>
                                <div className="text-[11px] text-muted-foreground">{k.sub}</div>
                            </div>
                        </label>
                    ))}
                </div>
                <div>
                    <Label className="text-xs">Reason (required)</Label>
                    <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="e.g. Thermoform machine BIOSTAR-001 vacuum pump failed at 14:30, awaiting maintenance" data-testid="workflow-hold-reason" />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button
                        onClick={() => onHold({ kind, reason })}
                        disabled={!kind || reason.trim().length < 5 || busy}
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                        data-testid="workflow-hold-submit"
                    >
                        <Pause size={12} className="mr-1.5" weight="fill" /> Mark on hold
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* ─── Escalate dialog — Layer 3. Sends to admin via aggregator. iter97.16
 * adds issue-type taxonomy (file/data, equipment, material, prescription,
 * other) so admin can triage faster, plus photo evidence upload. */
function EscalateDialog({ open, onClose, busy, onEscalate, caseId }) {
    const [reason, setReason] = useState("");
    const [severity, setSeverity] = useState("normal");
    const [issueType, setIssueType] = useState("other");
    const [photoIds, setPhotoIds] = useState([]);
    useEffect(() => {
        if (open) {
            setReason(""); setSeverity("normal"); setIssueType("other"); setPhotoIds([]);
        }
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="workflow-escalate-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Siren size={16} weight="fill" className="text-rose-600" />
                        Flag issue to admin
                    </DialogTitle>
                    <DialogDescription>
                        Per spec, file/data issues must be escalated within 15 minutes of discovery. Admin will see this on the notification bell.
                    </DialogDescription>
                </DialogHeader>

                <div>
                    <Label className="text-xs">Issue type</Label>
                    <Select value={issueType} onValueChange={setIssueType}>
                        <SelectTrigger className="mt-1" data-testid="workflow-escalate-issue-type">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="file_data">File / data error</SelectItem>
                            <SelectItem value="equipment">Equipment failure</SelectItem>
                            <SelectItem value="material">Material shortage</SelectItem>
                            <SelectItem value="prescription">Prescription conflict</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div>
                    <Label className="text-xs">Severity</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                        {[{ s: "normal", label: "Normal", cls: "border-amber-300 bg-amber-50/50 text-amber-900" },
                          { s: "critical", label: "Critical", cls: "border-rose-300 bg-rose-50/50 text-rose-900" }].map(({ s, label, cls }) => (
                            <label key={s} className={`p-2 rounded-lg border-2 cursor-pointer text-center text-xs font-bold uppercase tracking-wider ${severity === s ? cls : "border-border bg-card text-muted-foreground"}`}>
                                <input type="radio" name="esc-sev" value={s} checked={severity === s} onChange={(e) => setSeverity(e.target.value)} className="sr-only" data-testid={`escalate-sev-${s}`} />
                                {label}
                            </label>
                        ))}
                    </div>
                </div>

                <div>
                    <Label className="text-xs">What's the issue? (required)</Label>
                    <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} placeholder="e.g. Doctor's STL is missing UR3 — cannot proceed with stages 4-12" data-testid="workflow-escalate-reason" />
                </div>

                <PhotoUploadField
                    caseId={caseId}
                    kind="production_escalation"
                    value={photoIds}
                    onChange={setPhotoIds}
                    label="Evidence photos (optional)"
                    helperText="Photos help admin diagnose faster — e.g. equipment defect, material lot, file viewer screenshot."
                    testid="workflow-escalate-photos"
                    disabled={busy}
                />

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button
                        onClick={() => onEscalate({ reason, severity, issue_type: issueType, attachment_file_ids: photoIds })}
                        disabled={reason.trim().length < 5 || busy}
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                        data-testid="workflow-escalate-submit"
                    >
                        <Siren size={12} className="mr-1.5" weight="fill" /> Send to admin
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* ─── PriorityChip — small standalone component used by queue rows + detail
   hero. Renders nothing for standard priority (it's the common case). */
export function PriorityChip({ priority, size = "sm", testId }) {
    if (!priority || priority === "standard") return null;
    const tone = priority === "stat"
        ? { bg: "bg-rose-600", text: "text-white", label: "STAT" }
        : { bg: "bg-amber-500", text: "text-white", label: "Rush" };
    const cls = size === "lg"
        ? "px-2.5 py-1 text-[10px]"
        : "px-1.5 py-0.5 text-[9px]";
    return (
        <span
            className={`inline-flex items-center gap-1 ${tone.bg} ${tone.text} font-bold uppercase tracking-wider rounded ${cls}`}
            title={`Priority: ${tone.label}`}
            data-testid={testId || `priority-chip-${priority}`}
        >
            <Lightning size={size === "lg" ? 11 : 9} weight="fill" /> {tone.label}
        </span>
    );
}
